import React from 'react';
import {
  VirtualFAE as CEVirtualFAE,
  useHtmlTheme,
  useUserStoreSnapshot,
} from '@arm/ce-shared-components';
import { getVirtualFAEScriptUrl } from '../../../config/virtualFAEConfig';
import { UseGlobalState } from '../../../context';

/**
 * Dev2.0 JSS wrapper around the @arm/ce-shared-components VirtualFAE
 * (chat-ai) widget.
 *
 * Pure consumer of the cross-app `user-store` sessionStorage slot. The slot
 * is populated by `UserStoreBridge` (mounted inside `SupportAuth`), which
 * runs `useUserStoreAuthBridge`, `useUserStoreContextSync`, and
 * `useUserStoreEntitlements` on every support-tagged page. We subscribe via
 * `useUserStoreSnapshot` so this component re-renders the instant any field
 * changes (including cross-window writes from the login iframe). The
 * AzureB2C UseGlobalState path is kept as a defensive fallback for legacy
 * callers.
 */
export const VirtualFAE = ({ data }) => {
  const { appName, redirectUrl, title } = data;
  const theme = useHtmlTheme();
  const { user } = UseGlobalState();
  const scriptUrl = getVirtualFAEScriptUrl();

  // Live snapshot -- re-renders on any user-store write from any frame.
  const snapshot = useUserStoreSnapshot();

  const storeEmail = snapshot?.email ?? null;
  const storeAccessToken = snapshot?.accessToken ?? null;
  const storeEntitlements = snapshot?.hasPackageEntitlements ?? null;
  const storeTimestamp = snapshot?.timestamp ?? null;
  const userStoreContext = snapshot?.userContext ?? null;

  // Store is "present" once the bridge has written at least an email.
  const isStorePresent = !!storeEmail;

  // -- Derived values (store takes priority; AzureB2C is fallback) ----------
  const isAuthenticated = isStorePresent || (user?.isAuth ?? false);
  const userEmail = storeEmail ?? user?.user?.emails ?? '';

  // UserStoreBridge guarantees `userContext` is populated once mounted.
  // Treat absent context as "feature off" rather than falling back to env --
  // the bridge is the single source of truth.
  const isAIAssistanceEnabled = userStoreContext?.isAIAssistanceEnabled ?? false;
  const isCheckEntitlementsEnabled = userStoreContext?.isCheckEntitlementsEnabled ?? false;

  // Entitlements are written by UserStoreBridge after a successful fetch
  // (which always stamps `timestamp = Date.now()`). Until that happens we
  // treat the result as "still loading" rather than "no entitlement", so the
  // widget doesn't flash mounted-then-hidden. We use `timestamp != null` --
  // not `hasPackageEntitlements !== null` -- because Support's
  // zustand-persist default state writes `{ hasPackageEntitlements: false,
  // timestamp: null }` into the shared slot.
  const haveCachedEntitlements = storeTimestamp !== null;
  const hasPackageEntitlements = storeEntitlements ?? false;
  const isEntitlementsLoading = isCheckEntitlementsEnabled && !haveCachedEntitlements;

  const isAuthSettled = isStorePresent || !!user?.isLoginDone;
  const hasRequiredToken = !!storeAccessToken;
  const entitlementsPass =
    !isCheckEntitlementsEnabled || (isAIAssistanceEnabled && hasPackageEntitlements);

  const shouldMount =
    !!scriptUrl &&
    (isAIAssistanceEnabled || isCheckEntitlementsEnabled) &&
    isAuthSettled &&
    isAuthenticated &&
    hasRequiredToken &&
    entitlementsPass &&
    !isEntitlementsLoading;

  return (
    <CEVirtualFAE
      scriptUrl={scriptUrl}
      appName={appName}
      redirectUrl={redirectUrl}
      loginHint={userEmail}
      shouldMount={shouldMount}
      theme={theme}
      title={title}
    />
  );
};
